<script setup lang="ts">
import { createPropertyFromPrompt } from '@/backend/createPropertyFromPrompt'
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import PromptEditor from '@/sharedComponents/PromptEditor/PromptEditor.vue'
import type { Mentionable } from '@/sharedComponents/ProseMirror/ProseMirror.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import DividerLine from '@/uiKit/DividerLine.vue'
import HotKey from '@/uiKit/HotKey.vue'
import ListMenuContainer from '@/uiKit/ListMenuContainer.vue'
import { onClickOutside } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap.mjs'
import { computed, ref, useTemplateRef } from 'vue'
import { useCurrentWorkspace } from '../Workspaces/useCurrentWorkspace'
import { FIELD_DEFAULT_TOOL } from '../WorkspaceSettings/propertyConfig'
import { TYPE_ICON } from './icons'
import type { Property } from './Properties/types'
import PropertyEditMenu from './PropertyEditMenu.vue'
import { tools } from './Tools/tool-registry'
import { useModelInputs } from './useModelInputs'
import { serializeProperty, useProject } from './useProject'
import { useProperty } from './useProperty'

const emit = defineEmits<{
  'click:outside': []
  submit: [Property]
}>()
const propertyStore = useProperty()

const rootEl = useTemplateRef('rootEl')
const { deactivate: deactivateFocusTrap } = useFocusTrap(rootEl, { immediate: true })

const autoPropertyPrompt = ref('')
const currentWorkspaceId = useCurrentWorkspace()
const projectStore = useProject()
const isCreatingProperty = ref(false)
const onCreateAutoProperty = async () => {
  invariant(projectStore.projectId, 'No current project')
  if (!autoPropertyPrompt.value.trim()) {
    return
  }

  isCreatingProperty.value = true
  const res = await createPropertyFromPrompt({
    projectId: projectStore.projectId,
    prompt: autoPropertyPrompt.value,
    workspaceId: currentWorkspaceId.value.id,
  })

  isCreatingProperty.value = false
  if (!res.ok) {
    toast.error('Failed to create property')
    return
  }

  const property = serializeProperty(res.data)
  const autoTool = tools[property.tool]
  if (autoTool.deprecated) {
    /**
     * The frontend has its own concept of a tool being deprecated, and this is not
     * respected by the backend. If the backend suggests a tool that we treat as deprecated,
     * we should fall back to the default tool for that property type.
     */
    property.tool = FIELD_DEFAULT_TOOL[property.type]
  }

  deactivateFocusTrap()
  propertyStore.editedProperty = property
}

const handleClickOutside = () => {
  propertyStore.editedProperty = null
  emit('click:outside')
}

onClickOutside(rootEl, handleClickOutside)

const { inputIdOptions } = useModelInputs()
const proseMirrorMentionables = computed<Mentionable[]>(() =>
  inputIdOptions.value.map((item) => ({
    id:
      item.data.group === 'Library'
        ? `P${item.data.propertyId}:E${item.data.id}`
        : `P${item.data.id}`,
    label: item.data.name,
    icon: TYPE_ICON[item.data.type],
    group: item.data.group,
  })),
)

const onCreateProperty = () => {
  invariant(propertyStore.editedProperty, 'Property is not set')
  emit('submit', propertyStore.editedProperty)
}
</script>

<template>
  <ListMenuContainer
    v-if="!propertyStore.editedProperty"
    ref="rootEl"
    :aria-busy="isCreatingProperty"
  >
    <div class="go-scrollbar flex h-32 max-h-32 flex-col overflow-y-auto p-1">
      <PromptEditor
        value=""
        class="h-max grow resize-none p-1"
        :class="[isCreatingProperty && 'opacity-50']"
        placeholder="What do you want to do?"
        :mentionable-inputs="proseMirrorMentionables"
        aria-label="Prompt"
        autofocus
        :readonly="isCreatingProperty"
        @input="autoPropertyPrompt = $event"
        @keydown.enter.exact.stop.prevent="onCreateAutoProperty"
      />
    </div>
    <DividerLine color="subtle" />
    <DarwinButton
      size="sm"
      variant="neutral"
      class="m-1"
      :loading="isCreatingProperty"
      @click="onCreateAutoProperty"
    >
      <div class="flex items-center gap-1.5">
        <div>Save and pre-create property</div>
        <HotKey
          hotkey="Enter"
          size="xs"
        />
      </div>
    </DarwinButton>
  </ListMenuContainer>
  <PropertyEditMenu
    v-else
    @click:outside="handleClickOutside"
    @close="handleClickOutside"
    @update="onCreateProperty"
  />
</template>
